<script setup lang="ts">
import { computed } from "vue";
import { CssVarColors } from "@/shared/themes/CssVarColors.interface";
import { PageBlock } from "@/views/blocks/PageBlock.interface";
import EditableElement from "@/views/page/EditableElement.vue";
import type { ScrollAnimationType } from "@/apps/page/page-project.interface";

// Import shared components
import Section from "@/views/blocks/shared/section/layout/section/Section.vue";
import FullSpacing from "@/views/blocks/shared/section/layout/spacing/FullSpacing.vue";
import Heading from "@/views/blocks/shared/section/typography/heading/Heading.vue";
import Paragraph from "@/views/blocks/shared/section/typography/paragraph/Paragraph.vue";
import Image from "@/views/blocks/shared/section/image/Image.vue";
import SecondaryButton from "@/views/blocks/shared/section/buttons/secondary-button/SecondaryButton.vue";
import type { SimpleCenteredProps } from "./SimpleCenteredProps";

const props = defineProps<{
    block: PageBlock<SimpleCenteredProps>;
    pageColors: CssVarColors;
    scrollAnimationType?: ScrollAnimationType;
}>();

const dataValues = computed(() => props.block.dataValues);
const styleValues = computed(() => props.block.styleValues);
</script>

<template>
    <Section
        :id="block.webId"
        :backgroundColor="styleValues.background"
        :scrollAnimationType="scrollAnimationType"
    >
        <FullSpacing>
            <div class="mx-auto max-w-2xl lg:max-w-7xl flex flex-col gap-y-10">
                <EditableElement
                    v-if="!dataValues.title.isHidden"
                    :blockId="block.id"
                    field-name="title"
                >
                    <Heading
                        :text="dataValues.title.data.text"
                        :textColor="styleValues.text"
                        :tag="dataValues.title.data.tag"
                        class="text-center text-lg/8 font-semibold"
                    />
                </EditableElement>
                <div
                    v-if="dataValues.logos.data.items.length > 0"
                    class="max-w-2xl lg:max-w-none mx-auto"
                >
                    <div class="flex flex-wrap justify-center gap-y-8 gap-x-10 sm:gap-y-10">
                        <EditableElement
                            v-for="logo in dataValues.logos.data.items"
                            :key="logo.id"
                            :blockId="block.id"
                            :field-name="`logos_${logo.id}`"
                            :hideable="false"
                        >
                            <Image
                                class="max-w-40 xl:max-w-none max-h-12 object-contain grayscale opacity-80 hover:opacity-100 hover:grayscale-0 transition-all duration-300"
                                :src="logo.src"
                                :alt="logo.alt"
                                width="224"
                                height="48"
                            />
                        </EditableElement>
                    </div>
                </div>
                <EditableElement
                    v-if="!dataValues.callToAction.isHidden"
                    :blockId="block.id"
                    field-name="callToAction"
                >
                    <div
                        class="flex flex-wrap justify-center gap-2 mx-auto px-4 py-1.5 text-sm/6 rounded-full w-fit cta-background"
                    >
                        <Paragraph
                            :text="dataValues.callToAction.data.text"
                            :textColor="styleValues.text"
                            class="hidden md:block"
                        />
                        <SecondaryButton
                            :href="dataValues.callToAction.data.href"
                            :label="dataValues.callToAction.data.label"
                            :textColor="styleValues.brand"
                            class="font-semibold"
                        />
                    </div>
                </EditableElement>
            </div>
        </FullSpacing>
    </Section>
</template>

<style scoped>
.cta-background {
    background-color: color-mix(in oklab, oklch(70.7% 0.022 261.325) 5%, transparent);
}
</style>
